import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { combineLatest, distinctUntilChanged, filter, Subject, takeUntil } from 'rxjs';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { FilesService } from '@services/files.service';
import { IIntegratonSensor } from '@services/integration-sensors.service';
import { IAccount } from '@services/accounts.service';
import { IWebhookIntegraton } from '@services/integrations.service';
import { NeuronsensorsService, SensorPayload } from '@services/neuronsensors.service';
import { siteSubscriptions } from '@services/siteSubscriptions';

@Component({
	selector: 'app-edit-sensor-neuron',
	templateUrl: './edit-sensor-neuron.component.html',
	styleUrls: ['./edit-sensor-neuron.component.scss']
})
export class EditSensorNeuronComponent implements OnInit, OnDestroy {

	/** Path to Sensor Images */
	public imagePathSensors(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.sensors).toLowerCase()
	}

	/** Path to Datapoint Images */
	public imagePathDatapoints(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.datapoints).toLowerCase()
	}

	/** Update the Image */
	@Output() imageUpdated = new EventEmitter<any>();

	/** Form Group */
	dialogForm: FormGroup;

	public SelectedSensor: IIntegratonSensor;
	private webhookIntegration: IWebhookIntegraton;
	private NeuronSensor: SensorPayload;

	/** Enable the New Picture Button */
	public enablePicture: boolean = true;
	/** Enable the Camera Button */
	public enableCamera: boolean = true;
	/** Enable the Delete Button */
	public enableDelete: boolean = false;

	/** Update Image Instantly */
	public numberRND: number = Math.random();

	/** new Image Selected */
	public newImage: boolean;

	/** File */
	public file: any
	/** Filename */
	public fileName: string
	/** Loading */
	public loading: boolean
	/** shortLink */
	public shortLink: any

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** selectedAccount */
	private selectedAccount: IAccount;

	/** selectedAccountGUID */
	private selectedAccountGUID: string;

	/** Control the button whils saving data */
	public SavingData: boolean = false

	/**
	 * Default Constructor
	 * @param fb Form Builder
	 * @param dialogRef Referense to the Dialog box
	 * @param globalFunctions 
	 * @param filesService 
	 * @param siteSubscriptions 
	 * @param neuronsensorsService NeuronsensorsService
	 * @param dialogData  object interface
	 */
	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<EditSensorNeuronComponent>,
		private globalFunctions: GlobalFunctions,
		private filesService: FilesService,
		private siteSubscriptions: siteSubscriptions,
		private neuronsensorsService: NeuronsensorsService,
		@Inject(MAT_DIALOG_DATA) public dialogData: { sensor: IIntegratonSensor, webhook: IWebhookIntegraton },
	) { }

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		this.SelectedSensor = this.dialogData.sensor;
		this.webhookIntegration = this.dialogData.webhook;

		combineLatest([
			this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.neuronsensorsService.getDDevice$(
				this.webhookIntegration.NeuronSensorAPI,
				this.webhookIntegration.NeuronSensorSystem,
				this.SelectedSensor.PublicSensorID).pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([account, device]) => !!account && !!device)
		).subscribe({
			next: ([account, device]) => {

				this.selectedAccount = account;
				this.selectedAccountGUID = this.selectedAccount.GUID;
				this.NeuronSensor = device[0];

				this.dialogForm = this.fb.group({
					SensorID: [this.SelectedSensor.SensorID || 0, Validators.required],
					PublicSensorID: [this.SelectedSensor.PublicSensorID || "", Validators.required],
					Name: [this.SelectedSensor.Name || "", [Validators.required]],
					Description: [this.SelectedSensor.Description || ""],
					Modified: [Math.trunc(Math.random() * 100)],
					Enabled: [this.SelectedSensor.Enabled || false],
					EnableOffline: [this.SelectedSensor.EnableOffline || false],
					ImageSrc: this.imagePathSensors() + this.SelectedSensor.SensorID + '?' + this.numberRND,

					// // NeuronSensor-specific fields can be added here as needed
					NeuronName: [this.NeuronSensor?.info.alias || ""],
					NeuronDescription: [this.NeuronSensor?.info.description || ""],
					NeuronImageURL: [this.NeuronSensor?.info.image || ''],
				});

			},
			error: (error) => {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → Error in one of the combined subscriptions`, false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		if (this.dialogForm.dirty) {
			this.globalFunctions.UserConfirm$('Lose any unsaved changes?').subscribe(OKResult => {
				if (OKResult) { this.dialogRef.close(); }
				// do nothing if result is false
			});
		} else {
			this.dialogRef.close();
		}
	}

	/**
	 * Name Arrow Click - copy the Neuron Sensor Name to the local Sensor Name field to allow saving back to the local sensor if desired
	 */
	onNameArrowClick() {
		this.dialogForm.patchValue({
			Name: this.dialogForm.get('NeuronName')?.value
		});
		this.dialogForm.markAsDirty();
	}

	/**
	 * Description Arrow Click - copy the Neuron Sensor Description to the local Sensor Description field to allow saving back to the local sensor if desired
	 */
	onDescriptionArrowClick() {
		this.dialogForm.patchValue({
			Description: this.dialogForm.get('NeuronDescription')?.value
		});
		this.dialogForm.markAsDirty();
	}

	/** 
	 * Image Arrow Click - copy the Neuron Sensor Image to the local Sensor Image field to allow saving back to the local sensor if desired
	 */
	async onImageArrowClick() {
		// Create a 'Local'/shallow copy of the location Information
		try {

			var resultFile = await this.DownloadBlobfromURL(this.dialogForm.get('NeuronImageURL')?.value);
			this.file = resultFile;
			this.dialogForm.patchValue({
				ImageSrc: this.dialogForm.get('NeuronImageURL')?.value
			});
			this.dialogForm.markAsDirty();
			this.newImage = true;
		}
		catch (err) { console.error(err); }
	}

	/**
	 * Download the BLOB object from the URL
	 * @param url URL to the image
	 */
	async DownloadBlobfromURL(url: string): Promise<File> {

		const response = await fetch(url);

		if (!response.ok) {
			throw new Error(`Failed to download image: ${response.statusText}`);
		}

		const blob = await response.blob();

		return new File(
			[blob],
			'image',
			{
				type: blob.type
			}
		);
	}

	/**
	 * Save the form data and image if needed
	 */
	async onSaveClick(): Promise<void> {

		if (this.SavingData == true) {
			return;
		}

		try {
			this.SavingData = true;

			if (this.newImage && this.file) {
				const accountGUID = this.selectedAccountGUID.toLowerCase();
				const fileName = this.SelectedSensor.SensorID.toString();
				const blobLocation = "sensors";
				await this.filesService.saveImage(accountGUID, fileName, this.file, blobLocation, true); // wait for upload to complete
				this.newImage = false;
				this.file = undefined;
			}

			// Now the upload is done, close the dialog with the form value
			this.dialogRef.close(this.dialogForm.value);

		} // Optional: handle upload failure (maybe prevent closing the dialog)
		catch (error) {
			this.globalFunctions.HandleError(error, `Error: EditSensorNeuronComponent:onSaveClick → Error: Error Saving Sensor Data`, true)
		} finally {
			this.SavingData = false;
		}

	}

	/**
	 * Delete Image
	 */
	deleteImg(): void {
		// Extract and store the file name
		this.fileName = "transparent.png";
		this.file = new File([""], "transparent.png", { type: "image/png" });
		this.dialogForm.markAsDirty();
		this.newImage = true;
	}

	/**
	 * New File deteced
	 * @param file File from child component
	 */
	onNewImage(file: File | null): void {

		/** Check for a valid file */
		if (file) {
			this.file = file;
			this.newImage = true
		}
		else {
			this.file = undefined;
			this.newImage = false;
		}
		this.dialogForm.markAsDirty();
	}
}
